/**
 * Sandbox Worker
 *
 * Executes user scripts inside a Web Worker for isolation.
 * The Worker cannot access the parent window's DOM, localStorage, cookies, etc.
 * Dangerous APIs (fetch, XMLHttpRequest, WebSocket) are blocked inside the worker.
 */

/**
 * The worker code is inlined as a string so we can create it from a Blob URL
 * without needing a separate file. This code runs inside the Worker context.
 */
const WORKER_CODE = `
"use strict";

// === Block dangerous globals ===
self.fetch = undefined;
self.XMLHttpRequest = undefined;
self.WebSocket = undefined;
self.EventSource = undefined;
self.importScripts = function() { throw new Error("importScripts is not allowed in sandbox"); };
self.navigator = undefined;
self.location = undefined;
self.caches = undefined;
self.indexedDB = undefined;

// Block dynamic code generation
const OrigFunction = Function;

// === Chai-style expect API ===
function createExpect(value) {
  const assert = (condition, message) => {
    if (!condition) throw new Error(message);
  };

  const expectObj = { to: {} };

  expectObj.to.equal = (expected) => {
    assert(value === expected, "Expected " + value + " to equal " + expected);
    return expectObj;
  };
  expectObj.to.eql = (expected) => {
    assert(JSON.stringify(value) === JSON.stringify(expected),
      "Expected " + JSON.stringify(value) + " to deeply equal " + JSON.stringify(expected));
    return expectObj;
  };
  expectObj.to.be = {
    a: (type) => { assert(typeof value === type, "Expected " + value + " to be a " + type); return expectObj; },
    an: (type) => { assert(typeof value === type, "Expected " + value + " to be an " + type); return expectObj; },
    true: () => { assert(value === true, "Expected " + value + " to be true"); return expectObj; },
    false: () => { assert(value === false, "Expected " + value + " to be false"); return expectObj; },
    null: () => { assert(value === null, "Expected " + value + " to be null"); return expectObj; },
    undefined: () => { assert(value === undefined, "Expected " + value + " to be undefined"); return expectObj; },
    above: (num) => { assert(value > num, "Expected " + value + " to be above " + num); return expectObj; },
    below: (num) => { assert(value < num, "Expected " + value + " to be below " + num); return expectObj; },
    oneOf: (list) => { assert(list.includes(value), "Expected " + value + " to be one of " + list); return expectObj; },
  };
  expectObj.to.have = {
    property: (prop) => { assert(prop in value, "Expected object to have property '" + prop + "'"); return expectObj; },
    lengthOf: (len) => { assert(value.length === len, "Expected length " + value.length + " to be " + len); return expectObj; },
  };
  expectObj.to.include = (item) => {
    if (typeof value === "string") {
      assert(value.includes(item), "Expected '" + value + "' to include '" + item + "'");
    } else if (Array.isArray(value)) {
      assert(value.includes(item), "Expected array to include " + item);
    }
    return expectObj;
  };
  expectObj.to.exist = () => {
    assert(value !== null && value !== undefined, "Expected " + value + " to exist");
    return expectObj;
  };
  expectObj.to.not = {
    equal: (expected) => { assert(value !== expected, "Expected " + value + " to not equal " + expected); return expectObj; },
    be: {
      null: () => { assert(value !== null, "Expected " + value + " to not be null"); return expectObj; },
      undefined: () => { assert(value !== undefined, "Expected " + value + " to not be undefined"); return expectObj; },
    },
    include: (item) => {
      if (typeof value === "string") {
        assert(!value.includes(item), "Expected '" + value + "' to not include '" + item + "'");
      } else if (Array.isArray(value)) {
        assert(!value.includes(item), "Expected array to not include " + item);
      }
      return expectObj;
    },
  };

  return expectObj;
}

// === Build pm API from context data ===
function buildPmApi(context, result) {
  const envVars = new Map();
  if (context.environment && context.environment.variables) {
    context.environment.variables.forEach(function(v) {
      if (v.enabled) envVars.set(v.key, v.value);
    });
  }

  const globalVars = new Map();
  if (context.globalEnvironment && context.globalEnvironment.variables) {
    context.globalEnvironment.variables.forEach(function(v) {
      if (v.enabled) globalVars.set(v.key, v.value);
    });
  }

  const colVars = new Map();
  if (context.collectionVariables) {
    context.collectionVariables.forEach(function(v) {
      if (v.enabled) colVars.set(v.key, v.value);
    });
  }

  const requestHeaders = context.request.headers ? context.request.headers.slice() : [];

  return {
    environment: {
      get: function(key) { return envVars.get(key) !== undefined ? envVars.get(key) : result.variableUpdates.environment[key]; },
      set: function(key, value) { result.variableUpdates.environment[key] = value; envVars.set(key, value); },
      unset: function(key) { delete result.variableUpdates.environment[key]; envVars.delete(key); },
      has: function(key) { return envVars.has(key) || key in result.variableUpdates.environment; },
    },
    collectionVariables: {
      get: function(key) { return colVars.get(key) !== undefined ? colVars.get(key) : result.variableUpdates.collection[key]; },
      set: function(key, value) { result.variableUpdates.collection[key] = value; colVars.set(key, value); },
      unset: function(key) { delete result.variableUpdates.collection[key]; colVars.delete(key); },
      has: function(key) { return colVars.has(key) || key in result.variableUpdates.collection; },
    },
    globals: {
      get: function(key) { return globalVars.get(key) !== undefined ? globalVars.get(key) : result.variableUpdates.global[key]; },
      set: function(key, value) { result.variableUpdates.global[key] = value; globalVars.set(key, value); },
      unset: function(key) { delete result.variableUpdates.global[key]; globalVars.delete(key); },
      has: function(key) { return globalVars.has(key) || key in result.variableUpdates.global; },
    },
    request: {
      url: context.request.url,
      method: context.request.method,
      body: context.request.body && context.request.body.raw ? context.request.body.raw.content : undefined,
      headers: {
        add: function(name, value) {
          if (!result.modifiedRequest) result.modifiedRequest = {};
          if (!result.modifiedRequest.headers) result.modifiedRequest.headers = requestHeaders.slice();
          result.modifiedRequest.headers.push({ key: name, value: value, enabled: true });
        },
        get: function(name) {
          var lower = name.toLowerCase();
          var header = requestHeaders.find(function(h) { return h.key.toLowerCase() === lower; });
          return header ? header.value : undefined;
        },
        remove: function(name) {
          var lower = name.toLowerCase();
          if (!result.modifiedRequest) result.modifiedRequest = {};
          if (!result.modifiedRequest.headers) result.modifiedRequest.headers = requestHeaders.slice();
          result.modifiedRequest.headers = result.modifiedRequest.headers.filter(function(h) {
            return h.key.toLowerCase() !== lower;
          });
        },
      },
    },
    response: context.response ? {
      status: context.response.status,
      statusText: context.response.statusText,
      headers: context.response.headers,
      responseTime: context.response.time,
      responseSize: context.response.size,
      text: function() { return context.response.body; },
      json: function() {
        try { return JSON.parse(context.response.body); }
        catch(e) { throw new Error("Response body is not valid JSON"); }
      },
    } : undefined,
    test: function(name, fn) {
      try {
        fn();
        result.tests.push({ name: name, passed: true });
      } catch (e) {
        result.tests.push({ name: name, passed: false, error: e instanceof Error ? e.message : String(e) });
      }
    },
    expect: function(value) { return createExpect(value); },
    info: {
      requestName: context.request.name || "",
      requestId: context.request.id || "",
    },
  };
}

// === Console that captures logs ===
function buildConsole(result) {
  return {
    log: function() { result.logs.push({ level: "log", args: Array.from(arguments) }); },
    info: function() { result.logs.push({ level: "info", args: Array.from(arguments) }); },
    warn: function() { result.logs.push({ level: "warn", args: Array.from(arguments) }); },
    error: function() { result.logs.push({ level: "error", args: Array.from(arguments) }); },
  };
}

// === Message handler ===
self.onmessage = function(event) {
  var data = event.data;
  var script = data.script;
  var context = data.context;

  var result = {
    success: true,
    logs: [],
    tests: [],
    variableUpdates: { environment: {}, collection: {}, global: {} },
  };

  var pm = buildPmApi(context, result);
  var scriptConsole = buildConsole(result);

  try {
    var wrappedScript = '"use strict"; return (async function(pm, console) { ' + script + ' })(pm, console);';
    var fn = new OrigFunction("pm", "console", wrappedScript);

    var execPromise = fn(pm, scriptConsole);
    if (execPromise && typeof execPromise.then === "function") {
      execPromise.then(function() {
        self.postMessage({ type: "result", result: result });
      }).catch(function(error) {
        result.success = false;
        result.error = error instanceof Error ? error.message : String(error);
        self.postMessage({ type: "result", result: result });
      });
    } else {
      self.postMessage({ type: "result", result: result });
    }
  } catch (error) {
    result.success = false;
    result.error = error instanceof Error ? error.message : String(error);
    self.postMessage({ type: "result", result: result });
  }
};
`;

export interface WorkerScriptContext {
  request: {
    id?: string;
    name?: string;
    url: string;
    method: string;
    headers: Array<{ key: string; value: string; enabled: boolean }>;
    body?: { raw?: { content: string } };
  };
  response?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
    time: number;
    size: number;
  };
  environment?: {
    variables: Array<{ key: string; value: string; enabled: boolean }>;
  };
  globalEnvironment?: {
    variables: Array<{ key: string; value: string; enabled: boolean }>;
  };
  collectionVariables?: Array<{ key: string; value: string; enabled: boolean }>;
}

export interface WorkerScriptResult {
  success: boolean;
  logs: Array<{ level: string; args: any[] }>;
  tests: Array<{ name: string; passed: boolean; error?: string }>;
  error?: string;
  modifiedRequest?: {
    headers?: Array<{ key: string; value: string; enabled: boolean }>;
  };
  variableUpdates: {
    environment: Record<string, string>;
    collection: Record<string, string>;
    global: Record<string, string>;
  };
}

let workerBlobUrl: string | null = null;

function getWorkerBlobUrl(): string {
  if (!workerBlobUrl) {
    const blob = new Blob([WORKER_CODE], { type: 'application/javascript' });
    workerBlobUrl = URL.createObjectURL(blob);
  }
  return workerBlobUrl;
}

/**
 * Execute a script in a sandboxed Web Worker.
 * The Worker is created, runs the script, returns results, and is terminated.
 */
export function executeInWorker(
  script: string,
  context: WorkerScriptContext,
  timeout: number = 5000
): Promise<WorkerScriptResult> {
  return new Promise((resolve) => {
    const worker = new Worker(getWorkerBlobUrl());

    const timeoutId = setTimeout(() => {
      worker.terminate();
      resolve({
        success: false,
        error: 'Script execution timeout',
        logs: [],
        tests: [],
        variableUpdates: { environment: {}, collection: {}, global: {} },
      });
    }, timeout);

    worker.onmessage = (event) => {
      clearTimeout(timeoutId);
      worker.terminate();
      resolve(event.data.result as WorkerScriptResult);
    };

    worker.onerror = (event) => {
      clearTimeout(timeoutId);
      worker.terminate();
      resolve({
        success: false,
        error: event.message || 'Worker execution error',
        logs: [],
        tests: [],
        variableUpdates: { environment: {}, collection: {}, global: {} },
      });
    };

    worker.postMessage({ script, context });
  });
}

/**
 * Check if Web Workers are available in the current environment.
 */
export function isWorkerAvailable(): boolean {
  return typeof Worker !== 'undefined';
}
